import { CreateAttachmentsFunc } from '../types/CreateAttachmentsFunc';
import { logUsage } from 'owa-analytics';
import { AttachmentFileType, LocalComputerFile } from 'owa-attachment-file-types';
import {
    AttachmentWellViewState,
    lazyGetAvailableLocalAttachmentSize,
    CreateAttachmentsProperties,
} from 'owa-attachment-well-data';
import {
    CreateAttachmentHandler,
    lazyAttachmentActionsPanel,
    SelectedFilesSource,
    SizeLimitInKbToSuggestSharing,
    ComposeUniqueState,
} from 'owa-attachment-filepicker';

export default function getCreateAttachmentHandlerForLocalFile(
    viewState: AttachmentWellViewState,
    createAttachment: CreateAttachmentsFunc,
    composeUniqueState: ComposeUniqueState
): CreateAttachmentHandler {
    return async createAttachmentHandlerParams => {
        const fileObjects: File[] = [];
        for (let i = 0; i < createAttachmentHandlerParams.files.length; i++) {
            if (createAttachmentHandlerParams.files[i].fileType === AttachmentFileType.Local) {
                fileObjects.push(
                    (<LocalComputerFile>createAttachmentHandlerParams.files[i]).fileObject
                );
            } else {
                throw new Error(
                    `Unexpected file type ${createAttachmentHandlerParams.files[i].fileType}`
                );
            }
        }
        const totalSizeInBytes = fileObjects.reduce((sum, file) => sum + file.size, 0);
        const availableClassicAttachmentSizeInBytes = (
            await lazyGetAvailableLocalAttachmentSize.import()
        )(viewState);
        // We only show the dialog if
        // 1- The file sizes exceeds the size that can be attached OR
        // 2- The total size exceed the limit for suggesting to share
        const doesFilesSizeExceedsAvailableSize =
            totalSizeInBytes > availableClassicAttachmentSizeInBytes;
        const shouldSuggestSharing = totalSizeInBytes > SizeLimitInKbToSuggestSharing * 1024;
        const shouldShowActionPanel = doesFilesSizeExceedsAvailableSize || shouldSuggestSharing;

        const createAttachmentHandler: CreateAttachmentHandler = params => {
            const baseProperties = {
                isInline: params.isInline,
                isHiddenInline: false,
            };
            const properties: CreateAttachmentsProperties = params.shouldShare
                ? { ...baseProperties, shouldShare: true }
                : { ...baseProperties, shouldShare: false };

            createAttachment(params.files, properties, params.dataProviderType);
        };

        if (shouldShowActionPanel) {
            logUsage('AFPSecondPanelShownForLargeFiles', {
                suggestSharing: shouldSuggestSharing,
                exceedsAvailbleSize: doesFilesSizeExceedsAvailableSize,
                fileCount: Math.min(fileObjects.length, 23),
            });
            (await lazyAttachmentActionsPanel.import())(
                createAttachmentHandler,
                null /* insertLinkHandler */,
                fileObjects,
                null,
                SelectedFilesSource.LocalComputerFiles,
                availableClassicAttachmentSizeInBytes,
                composeUniqueState
            );
        } else {
            createAttachmentHandler(createAttachmentHandlerParams);
        }
    };
}
